import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

const COOKIE_CONSENT_KEY = "cookie-consent";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

const LANDING_ROUTES = ["/", "/pricing", "/industries", "/features", "/contact", "/help", "/terms", "/privacy", "/cookie-policy", "/b/"];

function isLandingRoute(pathname: string) {
  return LANDING_ROUTES.some(r => r === "/" ? pathname === "/" : pathname.startsWith(r));
}

async function syncConsentToDatabase(prefs: CookiePreferences) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from("cookie_consent").upsert(
      {
        user_id: user.id,
        necessary: prefs.necessary,
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        preferences: prefs.preferences,
        user_agent: navigator.userAgent,
        consented_at: new Date().toISOString(),
      },
      { onConflict: "user_id" }
    );
  } catch {
    // Silently fail - localStorage is the primary store
  }
}

async function loadConsentFromDatabase(): Promise<CookiePreferences | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data } = await supabase
      .from("cookie_consent")
      .select("necessary, analytics, marketing, preferences")
      .eq("user_id", user.id)
      .single();

    if (data) {
      return {
        necessary: data.necessary,
        analytics: data.analytics,
        marketing: data.marketing,
        preferences: data.preferences,
      };
    }
  } catch {
    // Silently fail
  }
  return null;
}

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  // Check auth state
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setIsAuthenticated(!!data.user));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session?.user);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Listen for external open-cookie-settings events
  useEffect(() => {
    const handleOpenSettings = () => {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) {
        try { setPreferences(JSON.parse(stored)); } catch {}
      }
      setShowSettings(true);
    };
    window.addEventListener("open-cookie-settings", handleOpenSettings);
    return () => window.removeEventListener("open-cookie-settings", handleOpenSettings);
  }, []);

  useEffect(() => {
    const init = async () => {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) {
        try {
          setPreferences(JSON.parse(stored));
          setHasConsented(true);
          return;
        } catch { /* fall through */ }
      }

      const dbConsent = await loadConsentFromDatabase();
      if (dbConsent) {
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(dbConsent));
        setPreferences(dbConsent);
        setHasConsented(true);
        window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: dbConsent }));
        return;
      }

      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    };
    init();
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    setHasConsented(true);
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: prefs }));
    syncConsentToDatabase(prefs);
  };

  const acceptAll = () => savePreferences({ necessary: true, analytics: true, marketing: true, preferences: true });
  const rejectNonEssential = () => savePreferences({ necessary: true, analytics: false, marketing: false, preferences: false });
  const saveCustomPreferences = () => savePreferences(preferences);

  // Show floating cookie icon on landing pages for unauthenticated users who have already consented
  const showFloatingIcon = hasConsented && !isAuthenticated && isLandingRoute(location.pathname) && !showBanner && !showSettings;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-card border-t shadow-lg animate-in slide-in-from-bottom-5 duration-300">
          <div className="container mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-foreground">We value your privacy</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
                    By clicking "Accept All", you consent to our use of cookies. Read our{" "}
                    <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>{" "}and{" "}
                    <Link to="/cookie-policy" className="text-primary hover:underline">Cookie Policy</Link> for more information.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
                <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} className="flex items-center justify-center gap-1 w-full sm:w-auto">
                  <Settings className="w-4 h-4" />
                  Customize
                </Button>
                <Button variant="outline" size="sm" onClick={rejectNonEssential} className="w-full sm:w-auto">
                  Reject Non-Essential
                </Button>
                <Button size="sm" onClick={acceptAll} className="w-full sm:w-auto">
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Cookie Icon for landing pages */}
      {showFloatingIcon && (
        <button
          onClick={() => {
            const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
            if (stored) {
              try { setPreferences(JSON.parse(stored)); } catch {}
            }
            setShowSettings(true);
          }}
          className="fixed bottom-4 left-4 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-card border border-border shadow-lg hover:shadow-xl transition-all hover:scale-110"
          aria-label="Cookie preferences"
        >
          <Cookie className="h-5 w-5 text-primary" />
        </button>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Cookie className="w-5 h-5" />Cookie Preferences</DialogTitle>
            <DialogDescription>
              Manage your cookie preferences. You can enable or disable different types of cookies below.
              Read our <Link to="/cookie-policy" className="text-primary hover:underline" onClick={() => setShowSettings(false)}>Cookie Policy</Link> for full details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-start justify-between gap-4">
              <div><Label className="text-base font-medium">Necessary Cookies</Label><p className="text-sm text-muted-foreground mt-1">Essential for the website to function properly. Cannot be disabled.</p></div>
              <Switch checked disabled className="data-[state=checked]:bg-muted" />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div><Label className="text-base font-medium">Analytics Cookies</Label><p className="text-sm text-muted-foreground mt-1">Help us understand how visitors interact with our website.</p></div>
              <Switch checked={preferences.analytics} onCheckedChange={(checked) => setPreferences((p) => ({ ...p, analytics: checked }))} />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div><Label className="text-base font-medium">Marketing Cookies</Label><p className="text-sm text-muted-foreground mt-1">Used to track visitors across websites to display relevant advertisements.</p></div>
              <Switch checked={preferences.marketing} onCheckedChange={(checked) => setPreferences((p) => ({ ...p, marketing: checked }))} />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div><Label className="text-base font-medium">Preference Cookies</Label><p className="text-sm text-muted-foreground mt-1">Allow the website to remember choices you make.</p></div>
              <Switch checked={preferences.preferences} onCheckedChange={(checked) => setPreferences((p) => ({ ...p, preferences: checked }))} />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={rejectNonEssential} className="w-full sm:w-auto">Reject All</Button>
            <Button onClick={saveCustomPreferences} className="w-full sm:w-auto">Save Preferences</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Exported function to open cookie settings from anywhere
export function openCookieSettings() {
  window.dispatchEvent(new Event("open-cookie-settings"));
}

// Hook to check cookie consent
export function useCookieConsent() {
  const [consent, setConsent] = useState<CookiePreferences | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try { setConsent(JSON.parse(stored)); } catch { setConsent(null); }
    }

    const handleUpdate = (e: CustomEvent<CookiePreferences>) => { setConsent(e.detail); };
    window.addEventListener("cookie-consent-updated", handleUpdate as EventListener);
    return () => window.removeEventListener("cookie-consent-updated", handleUpdate as EventListener);
  }, []);

  return consent;
}
